import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requestLoginCode } from "../lib/api";
import { InlineSpinner } from "../components/Loading";
import {
  ArrowRightIcon,
  Eyebrow,
  HeadingItalic,
  Wordmark,
} from "../components/design";

// /login — Step 1 of the new 2-step email-code login.
// Submits the email to /auth/request-code, then routes to /login/verify
// regardless of whether a code was actually sent (no email enumeration).

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const trimmed = email.trim();
    try {
      await requestLoginCode(trimmed);
      navigate(`/login/verify?email=${encodeURIComponent(trimmed)}`);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Etwas ist schiefgelaufen.";
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="paper-bg"
      style={{
        minHeight: "100vh",
        fontFamily: "var(--f-body)",
        color: "var(--ink)",
      }}
    >
      <div
        style={{
          maxWidth: 460,
          margin: "0 auto",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "60px 22px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link to="/" aria-label="Vitanara Startseite">
            <Wordmark />
          </Link>
          <div
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 12,
              color: "var(--ink-faint)",
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              fontWeight: 600,
            }}
          >
            Mein Buch
          </div>
        </header>

        <main
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px 22px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 420 }}>
            <Eyebrow>Anmelden</Eyebrow>
            <HeadingItalic size={36}>
              Willkommen <em>zurück</em>
              <br />
              in Ihrer Familie.
            </HeadingItalic>
            <p
              style={{
                fontFamily: "var(--f-body)",
                fontSize: 16,
                lineHeight: 1.5,
                color: "var(--ink-soft)",
                margin: "0 0 24px",
              }}
            >
              Geben Sie die E-Mail-Adresse ein, mit der Sie das
              Biografie-Projekt bestellt haben. Wir schicken Ihnen einen
              6-stelligen Code per E-Mail.
            </p>

            <form
              onSubmit={onSubmit}
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
            >
              <div>
                <label
                  htmlFor="email"
                  style={{
                    display: "block",
                    fontFamily: "var(--f-ui)",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--ink)",
                    marginBottom: 8,
                  }}
                >
                  E-Mail-Adresse
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="thomas@beispiel.de"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    background: "var(--paper-50)",
                    border: "1px solid rgba(43,26,15,0.18)",
                    borderRadius: 10,
                    padding: "14px 16px",
                    fontFamily: "var(--f-body)",
                    fontSize: 17,
                    color: "var(--ink)",
                    outline: "none",
                  }}
                />
                <div
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 12,
                    color: "var(--ink-faint)",
                    marginTop: 6,
                  }}
                >
                  Wenn ein Konto existiert, ist gleich ein Code im Postfach.
                </div>
              </div>
              {error && (
                <p
                  role="alert"
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 13,
                    color: "var(--oxblood)",
                    margin: 0,
                  }}
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={busy || !email}
                className="btn-primary"
                style={{ width: "100%", boxSizing: "border-box" }}
              >
                {busy ? <InlineSpinner /> : "Code per E-Mail schicken"}
                {!busy && <ArrowRightIcon />}
              </button>
            </form>

            <p
              style={{
                marginTop: 18,
                textAlign: "center",
                fontFamily: "var(--f-ui)",
                fontSize: 13,
                color: "var(--ink-faint)",
              }}
            >
              Noch kein Konto?{" "}
              <Link to="/onboarding" style={{ color: "var(--oxblood)" }}>
                Geschenk einrichten
              </Link>
              .
            </p>
            <p
              style={{
                marginTop: 6,
                textAlign: "center",
                fontFamily: "var(--f-ui)",
                fontSize: 12,
                color: "var(--ink-faint)",
              }}
            >
              <Link to="/" style={{ color: "inherit" }}>
                Zurück
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
